import { QuestionCircleOutlined } from '@ant-design/icons';
import { useMount } from 'ahooks';
import {
  Drawer,
  Form,
  Input,
  PageHeader,
  Popconfirm,
  Select,
  Space,
  TreeSelect,
} from 'antd';
import { useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import { RevshopOrgMarkupDTO } from 'domains/markupDomain';
import { NetworkMember } from 'domains/networkDomain';
import { Organization, PLATFORM_ORG_ID } from 'domains/orgDomain';
import { keyBy } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import markupService from 'services/markupService';
import networkService from 'services/networkService';
import { listToTree } from 'utils/misc';
import { RULE_INTEGER } from 'utils/validators';
import { RevxSpin } from '../../../../components/RevxSpin';

type TreeNodeOrganization = Organization & {
  value: Organization['id'];
  title: Organization['name'];
  children?: TreeNodeOrganization[];
};
const formItemLayout = {
  labelCol: { span: 12 },
  wrapperCol: { span: 12 },
};
const formActionsLayout = {
  labelCol: { span: 7 },
  wrapperCol: { span: 16, offset: 7 },
};

const RevshopOrgMarkupTab = () => {
  const [open, setOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<RevshopOrgMarkupDTO>();
  const [createForOrgId, setCreateForOrgId] =
    useState<number | undefined>(undefined);
  const [currentItemSameForAll, setCurrentItemSameForAll] = useState(false);
  const [currentItemIsRevshopMember, setCurrentItemIsRevshopMember] =
    useState(false);
  const [dataSource, setDataSource] = useState<RevshopOrgMarkupDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [markupForm] = Form.useForm();
  const [revshopNetworkMembersMap, setRevshopNetworkMembersMap] = useState<{
    [key: number]: NetworkMember;
  }>({});

  useMount(async () => {
    setLoading(true);
    await getMarkupList();
    const revshopNetworkMembers = await networkService.getNetworksByMemberId(
      PLATFORM_ORG_ID
    );
    setRevshopNetworkMembersMap(keyBy(revshopNetworkMembers, 'memberId'));
    setLoading(false);
  });

  useEffect(() => {
    if (currentItem) {
      const { markupPct, appliedOrgId, marketplaceNetworks } = currentItem;
      let fieldsValue: Record<string | number, any> = {
        appliedOrgId,
        markupPct,
      };
      if (revshopNetworkMembersMap[appliedOrgId]) {
        setCurrentItemIsRevshopMember(true);
        const markupValueSet = new Set<number>();
        const marketplaceNetworkMarkupsMap = keyBy(
          marketplaceNetworks,
          'appliedOrgId'
        );
        for (const marketplaceNetworkMember of revshopNetworkMembersMap[
          appliedOrgId
        ].marketplaceNetworks) {
          const memberId = marketplaceNetworkMember.memberId;
          const markup = marketplaceNetworkMarkupsMap[memberId];
          if (markup) {
            markupValueSet.add(markup.markupPct);
            fieldsValue[memberId] = markup.markupPct;
          } else {
            markupValueSet.add(markupPct);
            fieldsValue[memberId] = markupPct;
          }
        }
        if (markupValueSet.size === 1) {
          setCurrentItemSameForAll(true);
          fieldsValue['markupPct'] = markupValueSet.values().next().value;
        } else {
          setCurrentItemSameForAll(false);
        }
      } else {
        setCurrentItemSameForAll(false);
        setCurrentItemIsRevshopMember(false);
      }

      markupForm.setFieldsValue(fieldsValue);
    } else {
      markupForm.resetFields();
    }
  }, [currentItem, markupForm, revshopNetworkMembersMap]);

  const fullOrgList = useAppSelector((state) => state?.org?.list || []);

  const [treeData, orgMap] = useMemo(() => {
    const treeNodeList: TreeNodeOrganization[] = fullOrgList.map((o) => ({
      ...o,
      value: o.id,
      title: o.name,
      children: undefined,
    }));

    const orgMap = keyBy<TreeNodeOrganization>(treeNodeList, 'id');
    return [listToTree<TreeNodeOrganization>(treeNodeList, orgMap), orgMap];
  }, [fullOrgList]);

  const columns: TableColumnsType<RevshopOrgMarkupDTO> = [
    {
      title: 'Organization Name',
      dataIndex: 'appliedOrgName',
    },
    {
      title: 'Revshop Markup',
      dataIndex: 'markupPct',
      valueType: 'percentage',
    },
    {
      title: t('common.actions'),
      key: 'actions',
      fixed: 'right',
      render: (value, record) => (
        <Space size="small">
          <RevxButton
            type="link"
            onClick={() => {
              setCurrentItem(record);
              showDrawer();
            }}
          >
            View
          </RevxButton>
        </Space>
      ),
    },
  ];

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    markupForm.resetFields();
    setCreateForOrgId(undefined);
    setOpen(false);
    setCurrentItemIsRevshopMember(false);
    setCurrentItemSameForAll(false);
    setLoading(false);
    setCurrentItem(undefined);
  };
  const getMarkupList = async () => {
    const data = await markupService.getRevshopOrgMarkup(0, 999);
    setDataSource(data.content);
  };
  const onRemove = async () => {
    setLoading(true);
    await markupService.removeMarkup(currentItem!.id);
    await getMarkupList();
    onClose();
  };
  return (
    <RevxSpin spinning={loading}>
      <PageHeader
        ghost={false}
        title={
          <span className="bold" style={{ fontSize: 15 }}>
            Organization a different revshop markup than the standard value:
          </span>
        }
        extra={[
          <RevxButton
            key="create"
            type="primary"
            onClick={() => {
              showDrawer();
              setCurrentItem(undefined);
            }}
          >
            {t('common.create')}
          </RevxButton>,
        ]}
      />

      <RevxTable<RevshopOrgMarkupDTO>
        columns={columns}
        dataSource={dataSource}
        pagination={false}
      />
      <Drawer
        title={`${currentItem ? 'Edit' : 'Create'} MarkUp`}
        placement="right"
        onClose={onClose}
        visible={open}
        width={500}
        getContainer={false}
        mask={true}
      >
        <Form
          form={markupForm}
          {...formItemLayout}
          onFinish={async (v) => {
            try {
              setLoading(true);
              if (currentItem) {
                await markupService.updateRevshopOrgMarkup({
                  id: currentItem.id,
                  version: currentItem.version,
                  markupPct: currentItemSameForAll ? v.markupPct : 0,
                  appliedOrgId: v.appliedOrgId,
                  marketplaceNetworks: currentItem.marketplaceNetworks.map(
                    (mn) => ({
                      ...mn,
                      markupPct: v[mn.appliedOrgId],
                    })
                  ),
                });
              } else {
                await markupService.createRevshopOrgMarkup({
                  markupPct: currentItemSameForAll ? v.markupPct : 0,
                  appliedOrgId: v.appliedOrgId,
                  marketplaceNetworks: currentItemIsRevshopMember
                    ? revshopNetworkMembersMap[
                        v.appliedOrgId
                      ].marketplaceNetworks.map((member) => ({
                        appliedOrgId: member.memberId,
                        markupPct: v[member.memberId],
                      }))
                    : [],
                });
              }
              await getMarkupList();
            } finally {
              onClose();
            }
          }}
        >
          <Form.Item
            label="Organization"
            name="appliedOrgId"
            rules={[{ required: true }]}
          >
            {currentItem === undefined ? (
              <TreeSelect
                showSearch
                filterTreeNode={(inputValue, treeNode) =>
                  treeNode!
                    .title!.toString()
                    .toLocaleLowerCase()
                    .includes(inputValue.toLocaleLowerCase())
                }
                style={{ width: 300 }}
                dropdownMatchSelectWidth={300}
                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                treeData={treeData}
                treeDefaultExpandAll
                treeNodeFilterProp="title"
                placeholder="Select organization"
                onChange={(value: number) => {
                  setCurrentItemIsRevshopMember(
                    Boolean(revshopNetworkMembersMap[value])
                  );
                  setCurrentItemSameForAll(true);
                  setCreateForOrgId(value);
                }}
              />
            ) : (
              <span>{currentItem.appliedOrgName}</span>
            )}
          </Form.Item>
          {currentItemIsRevshopMember ? (
            <>
              <Form.Item
                label="Same for all"
                name="markupPct"
                rules={[RULE_INTEGER]}
              >
                <Input
                  style={{ width: 200 }}
                  prefix="%"
                  addonBefore={
                    <Select
                      value={currentItemSameForAll ? 'yes' : 'no'}
                      onChange={(v) => {
                        setCurrentItemSameForAll(v === 'yes');
                        if (v === 'yes') {
                          for (const member of revshopNetworkMembersMap[
                            markupForm.getFieldValue('appliedOrgId')
                          ]?.marketplaceNetworks) {
                            markupForm.setFieldsValue({
                              [member.memberId]:
                                markupForm.getFieldValue('markupPct'),
                            });
                          }
                        }
                      }}
                    >
                      <Select.Option value="yes">Yes</Select.Option>
                      <Select.Option value="no">No</Select.Option>
                    </Select>
                  }
                  disabled={!currentItemSameForAll}
                  onChange={(e) => {
                    if (currentItemSameForAll) {
                      for (const member of revshopNetworkMembersMap[
                        markupForm.getFieldValue('appliedOrgId')
                      ].marketplaceNetworks) {
                        markupForm.setFieldsValue({
                          [member.memberId]: e.target.value,
                        });
                      }
                    }
                  }}
                />
              </Form.Item>
              {revshopNetworkMembersMap[
                currentItem?.appliedOrgId ||
                  createForOrgId ||
                  Number(Object.keys(revshopNetworkMembersMap)[0])
              ].marketplaceNetworks.map((marketplaceNetworkMember) => (
                <Form.Item
                  key={marketplaceNetworkMember.id}
                  label={orgMap[marketplaceNetworkMember.memberId].name}
                  name={marketplaceNetworkMember.memberId}
                  rules={[RULE_INTEGER, { required: true }]}
                >
                  <Input
                    style={{ width: 200 }}
                    prefix="%"
                    disabled={currentItemSameForAll}
                  />
                </Form.Item>
              ))}
            </>
          ) : (
            <Form.Item
              label={t('platform.markup.labels.markup')}
              name="markupPct"
              rules={[RULE_INTEGER, { required: true }]}
            >
              <Input style={{ width: 200 }} prefix="%" />
            </Form.Item>
          )}
          <Form.Item {...formActionsLayout}>
            <RevxButton
              type="primary"
              htmlType="submit"
              brandPurple
              className="mr-10"
            >
              {t('common.save')}
            </RevxButton>
            <RevxButton onClick={onClose} className="mr-10">
              {t('common.cancel')}
            </RevxButton>
            {currentItem?.id && (
              <>
                <Popconfirm
                  title={t('common.confirmQuestion')}
                  icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                  onConfirm={onRemove}
                  okText={t('common.ok')}
                  cancelText={t('common.cancel')}
                >
                  <RevxButton danger>{t('common.remove')}</RevxButton>
                </Popconfirm>
              </>
            )}
          </Form.Item>
        </Form>
      </Drawer>
    </RevxSpin>
  );
};

export default RevshopOrgMarkupTab;
